/**
 * The design band — the art-direction card that sits on the board immediately
 * above and outside the table.
 *
 * A LAYER, NOT AN ELEMENT. It renders inside `CameraLayer` in raw board units
 * (the shared camera transform does pan/zoom, so it can never drift from the
 * table) and derives entirely from the `design` map. That choice buys three
 * things an element could not:
 *
 *  - There is exactly ONE. Nothing to duplicate on a regeneration, nothing to
 *    drag into the void and lose, no per-client race to mint an element id.
 *  - Placement costs ZERO doc writes, so regenerating is a pure data change.
 *  - It can render palette swatches and labelled fields, which a `text-block`
 *    element's `{kind:"text"}` payload cannot.
 *
 * What it gives up, deliberately: the card itself is not draggable and cannot
 * carry a comment. Comments belong on the generated key visuals (phase 2),
 * which ARE real image elements for exactly that reason.
 *
 * BECAUSE it is not draggable, it is also POINTER-TRANSPARENT (`pointer-events:
 * none`, with only the head's two buttons — Copy and Edit — opted back in). An
 * undraggable object that still swallowed pointerdown would be a 560×264 hole
 * in the board where panning silently does nothing — every other pixel of empty
 * canvas pans, and a card that quietly refuses reads as a broken drag, not as a
 * deliberate one.
 * Drags now pass through to the dot layer beneath and pan normally.
 */
import { useEffect, useRef } from "react";

// Side-effect import: registers the per-row design action in the F-27 toolbar.
// Same pattern as TranslateLayer's `import "./action.js"`.
import "./action.js";
import { useStore } from "zustand";

import type { CanvasStore } from "../../core/canvas.js";
import type { BoardSession } from "../../core/session.js";
import type { GridLayoutSnapshot } from "../board/GridTable.js";
import { assetUrl } from "../../core/api.js";
import { iconDownload } from "../../ui/icons.js";
import { copyDesignDescription } from "./description.js";
import { downloadDesignShot } from "./download.js";
import {
  CARD_MAX_HEIGHT,
  designCardRect,
  framingPanDy,
  shotRects,
  SHOT_HEIGHT,
} from "./layout.js";
import type { DesignUiStore } from "./store.js";
import { useDesignTheme } from "./use-design.js";

export interface DesignBandLayerProps {
  session: BoardSession;
  ui: DesignUiStore;
  canvas: CanvasStore;
  /** Latest grid layout pass — sizes the card to the table. May be null. */
  layout: GridLayoutSnapshot | null;
}

/** Fields shown on the card. The full set stays in the panel. */
const CARD_FIELDS = ["perspective", "lighting", "texture", "motion"] as const;

export function DesignBandLayer({ session, ui, canvas, layout }: DesignBandLayerProps) {
  const theme = useDesignTheme(session);
  const panelOpen = useStore(ui, (s) => s.panelOpen);
  // Subscribed, not read once: the ResizeObserver publishes the viewport AFTER
  // the first render, and the framing effect below cannot do its arithmetic
  // until it has one. Without this dependency the effect's "try again next
  // pass" never gets another pass and the card silently stays off-screen.
  const viewportWidth = useStore(canvas, (s) => s.viewport.width);
  const shots = theme.latestRun;
  const rect = designCardRect(layout, shots.length);
  const strip = shotRects(shots, layout);
  // A shot's 1-based place in the GALLERY's storage order, not in the strip:
  // the download name has to match the panel's for the same page, and the
  // panel numbers from `theme.shots` (see DesignPanel's gallery).
  const positions = new Map(theme.shots.map((s, i) => [s.id, i + 1] as const));
  const cardRef = useRef<HTMLDivElement | null>(null);
  const framed = useRef(false);

  // Bring the card into view ONCE per board open, if it is off-screen above.
  // Without this the feature is invisible on every load: the default camera
  // frames the table, the card lives above it, and the camera is not
  // persisted. Runs at most once and never moves a camera the user has already
  // moved — being helpful on open must not mean yanking the view mid-work.
  useEffect(() => {
    if (framed.current || !theme.present) return;
    const card = cardRef.current;
    if (card === null) return;
    if (viewportWidth === 0) return; // not measured yet — re-runs when it is
    const { camera } = canvas.getState();
    framed.current = true;
    const cardTop = rect.bottom - card.offsetHeight;
    const dy = framingPanDy(camera.y, camera.zoom, cardTop);
    if (dy !== 0) canvas.getState().panBy(0, dy);
  }, [theme.present, canvas, rect.bottom, viewportWidth]);

  // Nothing generated yet: stay off the board entirely rather than parking an
  // empty placeholder above every board that will never use this feature.
  if (!theme.present) return null;

  return (
    <>
    <div
      ref={cardRef}
      className="design-band"
      // Pinned by its BOTTOM edge (CSS pulls the box up by its own height), so
      // the card grows with its content and the clearance below stays exact.
      style={{ left: rect.x, top: rect.bottom, width: rect.w, maxHeight: CARD_MAX_HEIGHT }}
    >
      <div className="design-band-head">
        <span className="design-band-kicker">Design theme</span>
        <span className="spacer" />
        {/* F-136: the card is the summary an animator reads before opening
            anything — so the "give me that as text" button belongs here too,
            not only in the panel. Interactive like Edit (the card itself is
            pointer-transparent; `design-band-edit` opts a control back in). */}
        <button
          type="button"
          className="design-band-edit"
          onClick={() => void copyDesignDescription(theme.artDirection)}
        >
          Copy
        </button>
        <button
          type="button"
          className="design-band-edit"
          onClick={() => ui.getState().setPanelOpen(!panelOpen)}
        >
          Edit
        </button>
      </div>

      {theme.artDirection.logline && (
        <p className="design-band-logline">{theme.artDirection.logline}</p>
      )}

      {theme.artDirection.palette.length > 0 && (
        <div className="design-band-palette">
          {theme.artDirection.palette.map((swatch, i) => (
            <span
              className="design-band-swatch"
              key={`${swatch.hex}-${i}`}
              style={{ background: swatch.hex }}
              data-tip={`${swatch.hex}${swatch.name ? ` — ${swatch.name}` : ""}`}
            />
          ))}
        </div>
      )}

      <dl className="design-band-grid">
        {CARD_FIELDS.map((field) => {
          const value = theme.artDirection[field];
          if (!value) return null;
          return (
            <div className="design-band-cell" key={field}>
              <dt>{field}</dt>
              <dd>{value}</dd>
            </div>
          );
        })}
      </dl>

      {theme.artDirection.avoid && (
        <p className="design-band-avoid">
          <strong>Avoid</strong> {theme.artDirection.avoid}
        </p>
      )}
    </div>

    {/* The run's key visuals, sitting between the card and the table. Real
        <img> elements rather than board elements: phase 3 promotes a shot to a
        draggable, commentable element on an explicit click, so nothing here
        races to mint element ids across clients. */}
    {shots.map((shot, i) => {
      const box = strip[i];
      if (box === undefined) return null;
      const position = positions.get(shot.id) ?? i + 1;
      const name = shot.label ?? `key visual ${position}`;
      return (
        <div
          key={shot.id}
          className="design-shot-wrap"
          // Bottom-anchored like the card (CSS pulls the box up by its own
          // height), so the clearance above the table is exact.
          style={{ left: box.x, top: box.bottom, width: box.w, height: SHOT_HEIGHT }}
        >
          <img
            className="design-shot"
            src={assetUrl(shot.assetId)}
            alt={shot.label ?? `Key visual ${position}`}
            // The page name first — the prompt below it is 60 lines and the
            // name is the only part worth reading at a glance.
            title={shot.label === undefined ? shot.prompt : `${shot.label}\n\n${shot.prompt}`}
          />
          {/* F-136: the same save the panel offers, ON the board. A key visual
              is the thing an animator actually leaves Kola with, and until now
              reaching it meant opening the design panel and hunting the page
              down in a 2-up gallery — while the full-size copy of it was right
              there under the pointer. Named identically to the panel's copy
              (both go through `downloadDesignShot`), so the same page saves
              under the same file name whichever button you press.

              The strip is pointer-transparent so drags over it still pan; this
              button opts itself back in, exactly as the card's Copy/Edit do. */}
          <button
            type="button"
            className="design-thumb-btn design-thumb-download design-shot-download"
            onClick={() => void downloadDesignShot(shot, position)}
            aria-label={`Download ${name}`}
            data-tip="Download"
          >
            {iconDownload}
          </button>
        </div>
      );
    })}
    </>
  );
}
